import { makeAutoObservable, runInAction } from 'mobx'

import { notificationService } from '@/Core'
import { createFormContext } from '@/Core/UI/Form/Providers'
import { serverMessage, serverStatus } from '@/Ship/Helpers/serverError'

import { LETTER_TEXTS as T } from '../Contracts/ILetter'
import { normalizeAddresses } from '../Helpers/letterAddresses'
import { buildLetterPatchBody, scheduledAtOf } from '../Helpers/letterPayload'
import { canEditLetter, canSendLetter } from '../Helpers/letterRights'
import { letterFieldErrors, letterViolationsOf } from '../Helpers/letterViolations'
import { LetterResource } from '../Resources/LetterResource'
import { GenerateLetterTask } from '../Tasks/GenerateLetterTask'
import { LoadLetterRecipientsTask } from '../Tasks/LoadLetterRecipientsTask'
import { LoadLetterTask } from '../Tasks/LoadLetterTask'
import { SendLetterTask } from '../Tasks/SendLetterTask'

import type { ILetter, LetterCounterparty } from '../Contracts/ILetter'
import type { LetterRecipientOption } from '../Helpers/letterAddresses'
import type { IFieldConfig } from '@/Core/Contracts/UI/Form'
import type { FormStore } from '@/Core/UI/Form/Stores/FormStore'

/**
 * Варіант шухляди (`SPEC-013` §9.4–§9.5): звичайний лист із карток власника
 * або лист документа — «Відправити документи», без відкладеної дати.
 */
export type LetterDrawerVariant = 'letter' | 'document'

export type LetterDrawerMode = 'view' | 'edit'

/** Поля шухляди в порядку легасі (`CompanyEmailFormTemplate.jsx:3-13`). */
export const LETTER_FORM_FIELDS = ['toWhom', 'cc', 'bcc', 'scheduledAt', 'subject', 'body', 'attachments'] as const

/** Перегляд: ті самі поля, плюс коли лист пішов і хто його автор. */
export const LETTER_VIEW_EXTRA_FIELDS = ['sentAt', 'author'] as const

/** Чий лист — контрагент, з чиїх контактів підказки адресатів. */
const counterpartyOf = (letter: ILetter): { counterparty: LetterCounterparty; uuid: string } | null => {
    if (typeof letter.client?.uuid === 'string') return { counterparty: 'client', uuid: letter.client.uuid }
    if (typeof letter.carrier?.uuid === 'string') return { counterparty: 'carrier', uuid: letter.carrier.uuid }

    return null
}

/**
 * Шухляда листа (`SPEC-013` §9.4–§9.5): відкритий лист, його форма, адресати
 * для підказок, «Відправити».
 *
 *  - **перегляд** — поля картки; відправлений лист показує те, що фактично
 *    пішло (`sentBody`: тіло й підпис);
 *  - **редагування** — лише за `_permissions.update` невідправленого листа;
 *    ядрових «Зберегти» / «Редагувати» немає: правки зберігає «Відправити».
 *    Закрили без відправки — чернетка лишається (її прибирає чистка за добу);
 *  - **«Відправити»** — перевірка форми, порожні адреси відкидаються, без
 *    адресатів — «Заповніть поле …»; далі `SendLetterTask`. Негайна —
 *    «Лист успішно відправлено.», відкладена — мовчки; шухляда закривається,
 *    вкладка перечитується;
 *  - **помилка** — «Помилка» + текст сервера, шухляда лишається відкритою,
 *    індикатор знімається (Л-19). 422 полів — під поля. Помилка SMTP робить
 *    лист «Не відправлено»: перечитуємо його, і над формою з'являється причина.
 */
export class LetterDrawerStore {
    letter: ILetter | null = null
    form: FormStore | null = null
    mode: LetterDrawerMode = 'view'
    variant: LetterDrawerVariant = 'letter'
    isLoading = false
    pending = false

    recipients: LetterRecipientOption[] = []
    recipientsLoading = false

    /** Що зробити після відправки, видалення чи закриття — перечитати вкладку */
    private onChanged: (() => void) | null = null

    /** Номер відкриття: відповідь на попереднє не перезаписує новіше */
    private openRequest = 0

    constructor() {
        makeAutoObservable<LetterDrawerStore, 'onChanged' | 'openRequest'>(
            this,
            { onChanged: false, openRequest: false },
            { autoBind: true }
        )
    }

    get isOpen(): boolean {
        return this.isLoading || this.letter !== null
    }

    get isEditable(): boolean {
        return this.mode === 'edit' && canEditLetter(this.letter)
    }

    get canSend(): boolean {
        return this.isEditable && canSendLetter(this.letter) && !this.isLoading
    }

    /** Поля шухляди: у листа документа немає відкладеної дати (§9.5). */
    get fieldNames(): string[] {
        const fields: string[] = LETTER_FORM_FIELDS.filter(
            (name) => !(this.variant === 'document' && name === 'scheduledAt')
        )

        return this.isEditable ? fields : [...fields, ...LETTER_VIEW_EXTRA_FIELDS]
    }

    /** Відкрити наявний лист — з рядка вкладки чи меню. */
    async open(uuid: string, mode: LetterDrawerMode, onChanged: () => void, variant: LetterDrawerVariant = 'letter'): Promise<void> {
        const request = ++this.openRequest

        this.onChanged = onChanged
        this.variant = variant
        this.letter = null
        this.form = null
        this.isLoading = true

        try {
            const letter = await LoadLetterTask(uuid)

            if (request !== this.openRequest) return

            runInAction(() => this.show(letter, mode))
        } catch (error: unknown) {
            if (request !== this.openRequest) return

            runInAction(() => {
                this.isLoading = false
            })
            notificationService.showError(T.error, serverMessage(error, T.contactAdmin))
        }
    }

    /**
     * Нова чернетка з шаблону (`SPEC-013` §6.1, §9.3): генерація → шухляда в
     * редагуванні. Права чернетки — з беку: якщо відповідь генерації їх не
     * несе, лист перечитуємо з `?permissions=true`.
     *
     * Повертає `false`, якщо генерація не вдалася (повідомлення вже показане).
     */
    async generate(
        templateUuid: string,
        entityUuid: string,
        onChanged: () => void,
        variant: LetterDrawerVariant = 'letter'
    ): Promise<boolean> {
        const request = ++this.openRequest

        try {
            const generated = await GenerateLetterTask(templateUuid, entityUuid)
            const letter =
                generated._permissions && typeof generated._permissions === 'object'
                    ? generated
                    : await LoadLetterTask(String(generated.uuid))

            if (request !== this.openRequest) return true

            runInAction(() => {
                this.onChanged = onChanged
                this.variant = variant
                this.show(letter, 'edit')
            })

            return true
        } catch (error: unknown) {
            notificationService.showError(T.error, serverMessage(error, T.contactAdmin))

            return false
        }
    }

    close(): void {
        if (this.pending) return

        const onChanged = this.onChanged

        this.openRequest++
        this.form?.dispose()
        this.letter = null
        this.form = null
        this.isLoading = false
        this.recipients = []
        this.onChanged = null

        onChanged?.()
    }

    /** «Відправити» (§9.4). */
    async send(): Promise<void> {
        const letter = this.letter
        const form = this.form

        if (!letter || !form || this.pending || !this.canSend || typeof letter.uuid !== 'string') return

        // Порожні рядки адрес відкидаємо до перевірки обов'язковості
        for (const field of ['toWhom', 'cc', 'bcc'] as const) {
            form.setValue(field, normalizeAddresses(form.getValue(field)))
        }

        // Без адресатів — текст легасі, а не загальне «поле обов'язкове»
        // (`SendCompanyEmailTask.js:24-27`); бек перевіряє те саме (§7.2)
        if (normalizeAddresses(form.getValue('toWhom')).length === 0) {
            form.setError('toWhom', T.fillToWhom)
            notificationService.showError(T.error, T.fillToWhom)
            return
        }

        if (!form.validate()) return

        const body = buildLetterPatchBody(form)

        const scheduledAt = this.variant === 'document' ? null : scheduledAtOf(form.getValue('scheduledAt'))

        this.pending = true

        try {
            const result = await SendLetterTask(letter.uuid, body, scheduledAt)

            runInAction(() => {
                this.pending = false
            })

            if (!result.scheduled) notificationService.showSuccess(T.success, T.sentSuccess)

            this.close()
        } catch (error: unknown) {
            const violations =
                serverStatus(error) === 422 ? letterViolationsOf((error as { response?: unknown }).response) : []
            const fieldErrors = letterFieldErrors(violations)

            runInAction(() => {
                this.pending = false
            })

            notificationService.showError(T.error, serverMessage(error, T.contactAdmin))

            if (Object.keys(fieldErrors).length > 0) {
                form.setErrors(fieldErrors)
                return
            }

            // Правки вже збережено, а лист міг стати «Не відправлено» —
            // перечитуємо його, щоб над формою з'явилася причина
            await this.refresh()
            this.onChanged?.()
        }
    }

    /** Перечитати відкритий лист, лишаючи шухляду в тому самому режимі. */
    private async refresh(): Promise<void> {
        const uuid = this.letter?.uuid

        if (typeof uuid !== 'string') return

        const request = this.openRequest

        try {
            const letter = await LoadLetterTask(uuid)

            if (request !== this.openRequest) return

            runInAction(() => this.show(letter, this.mode))
        } catch {
            // Лишається те, що на екрані, і вже показане повідомлення
        }
    }

    private show(letter: ILetter, mode: LetterDrawerMode): void {
        this.form?.dispose()

        this.letter = letter
        this.mode = mode === 'edit' && canEditLetter(letter) ? 'edit' : 'view'
        this.isLoading = false

        const fields = LetterResource.fields as Record<string, IFieldConfig>

        this.form = createFormContext({
            fields,
            isEditable: this.mode === 'edit',
            initialValues: {
                toWhom: normalizeAddresses(letter.toWhom),
                cc: normalizeAddresses(letter.cc),
                bcc: normalizeAddresses(letter.bcc),
                scheduledAt: letter.scheduledAt ?? null,
                subject: letter.subject ?? '',
                // Відправлений — те, що фактично пішло: тіло й підпис (§9.2)
                body: letter.status === 'sent' && typeof letter.sentBody === 'string' ? letter.sentBody : (letter.body ?? ''),
                attachments: Array.isArray(letter.attachments) ? letter.attachments : [],
                sentAt: letter.sentAt ?? null,
                author: letter.author ?? null,
            },
        }) as FormStore

        if (this.mode === 'edit') void this.loadRecipients(letter)
    }

    private async loadRecipients(letter: ILetter): Promise<void> {
        const owner = counterpartyOf(letter)

        if (owner === null) return

        this.recipientsLoading = true

        try {
            const options = await LoadLetterRecipientsTask(owner.counterparty, owner.uuid)

            runInAction(() => {
                if (this.letter?.uuid === letter.uuid) this.recipients = options
            })
        } finally {
            runInAction(() => {
                this.recipientsLoading = false
            })
        }
    }
}
